/**
 * @puffin-core/auth-client — canonical tenant auth-service factory.
 *
 * Transport-agnostic: consumes an injected `http` adapter (see `transports.js`)
 * + a `tokenStore` (@puffin-core/auth-token-storage). Returns the SUPERSET of
 * auth operations; each app's thin `authService.js` selects + aliases the shape
 * its consumers (and `createAuthContext`) need. The factory owns ALL endpoint
 * wiring, response extraction, error handling, and the dual verifyMfa/sendMfaOtp
 * projections — so no app re-implements auth HTTP.
 *
 * @module @puffin-core/auth-client/createAuthService
 */

import { AUTH_ENDPOINTS } from './endpoints.js';
import { createTokenGate } from './internal.js';
import { buildCoreOperations } from './operations/core.js';
import { buildMfaOperations } from './operations/mfa.js';
import { buildMfaSetupOperations } from './operations/mfaSetup.js';
import { buildDeviceOperations } from './operations/devices.js';
import { buildSecurityOperations } from './operations/security.js';
import { buildPasskeyOperations } from './operations/passkey.js';
import { buildTenantContextSurface } from './operations/contextSurface.js';

/**
 * The ONE device-token source of every credential sign-in (REGISTER-WALKIN-RESET finding 8). pos-api binds a session to
 * a registered device from the sign-in body's `deviceToken` (DRG-001), and store opening needs BOTH a device-bound
 * session and a password or passkey method — so a device app injects its token source. It is read at EACH sign-in
 * (a device registered after the service was built is seen), and the field rides only when there is a token, so an app
 * with no devices keeps its exact wire.
 *
 * @param {Function} [getDeviceToken] - Answers this device's token, or nothing.
 * @returns {function(): ({deviceToken: string}|{})} The body fields to spread into a credential sign-in.
 */
function createDeviceTokenField(getDeviceToken) {
  return () => {
    const deviceToken = getDeviceToken ? getDeviceToken() : null;
    return deviceToken ? { deviceToken } : {};
  };
}

/**
 * @param {Object} deps
 * @param {Function} deps.http - Transport adapter: ({method,path,body,auth,signal,query}) => Promise<{ok,status,body,networkError?}>
 * @param {Object} deps.tokenStore - @puffin-core/auth-token-storage instance
 * @param {Function} deps.errorTranslator - translateAuthError from @puffin-core/login-shell (REQUIRED for MFA classification)
 * @param {Object} [deps.endpoints] - Per-app overrides spread over AUTH_ENDPOINTS
 * @param {Object} [deps.deduper] - createInflightDeduper instance (wraps getSecurityOverview)
 * @param {never} [deps.setAuthHeader] - RETIRED (SETAUTHHEADER-RETIRE): passing the key throws a `TypeError`
 * @param {'result'|'throw'} [deps.mfaSetupShape='throw'] - verify/regenerate/disable shape
 * @param {string} [deps.refreshEndpoint] - REFRESH_ENDPOINT for refreshToken()
 * @param {Function} [deps.getDeviceToken] - This device's token, sent on every credential sign-in (device apps)
 * @returns {Object} The canonical auth-service object (superset).
 */
export function createAuthService(deps) {
  const {
    http,
    tokenStore,
    errorTranslator,
    endpoints,
    deduper,
    mfaSetupShape = 'throw',
    refreshEndpoint,
    getDeviceToken,
  } = deps || {};

  // SETAUTHHEADER-RETIRE (2026-10-26; console's Ruling 35, finished fleet-wide): the dep synced the stored bearer onto
  // `axios.defaults`. Nothing read it (`@puffin-core/api-client` attaches the token per request through `getToken`), and
  // every `axios.create()` built after a sign-in copied it. Refused by name, not ignored, so a caller still passing it
  // fails at boot instead of silently losing a behaviour it thinks it has.
  if (deps && Object.hasOwn(deps, 'setAuthHeader')) {
    throw new TypeError(
      'createAuthService: `setAuthHeader` is retired. A bearer on axios.defaults rides every client built after it; @puffin-core/api-client attaches the token per request through `getToken`.',
    );
  }
  if (typeof http !== 'function') {
    throw new TypeError('createAuthService requires a `http` transport function');
  }
  if (!tokenStore || typeof tokenStore.getAccessToken !== 'function') {
    throw new TypeError('createAuthService requires a `tokenStore`');
  }
  if (typeof errorTranslator !== 'function') {
    throw new TypeError('createAuthService requires an `errorTranslator` (translateAuthError)');
  }
  if (getDeviceToken !== undefined && typeof getDeviceToken !== 'function') {
    throw new TypeError('createAuthService: `getDeviceToken` must be a function when given');
  }

  const ctx = {
    request: http,
    endpoints: { ...AUTH_ENDPOINTS, ...(endpoints || {}) },
    tokenStore,
    errorTranslator,
    deduper,
    mfaSetupShape,
    refreshEndpoint,
    mustHaveToken: createTokenGate(tokenStore),
    deviceTokenField: createDeviceTokenField(getDeviceToken),
  };

  const service = {
    ...buildCoreOperations(ctx),
    ...buildMfaOperations(ctx),
    ...buildMfaSetupOperations(ctx),
    ...buildDeviceOperations(ctx),
    ...buildSecurityOperations(ctx),
    ...buildPasskeyOperations(ctx),
  };

  // AUTHSVC-CONSOLIDATION-002: the pre-aliased `createAuthContext` surface.
  // Apps spread `service.contextSurface` into the object they feed
  // `createAuthContext`, so the catastrophic-silent {verifyMfa, sendMfaOtp,
  // authenticateWithPasskey} alias mapping happens ONCE, here, never per app.
  // Frozen (E1 hindsight) — the alias mapping is the most security-critical
  // object in the frontend auth chain; freezing makes it tamper-evident. Apps
  // spread it (`{ ...service.contextSurface }`), so the copy stays mutable.
  service.contextSurface = Object.freeze(buildTenantContextSurface(service));

  return service;
}
